// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getDoc, deleteDoc, doc, addDoc, updateDoc, deleteField, documentId, serverTimestamp, orderBy, increment } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import EditPostModal from '../components/EditPostModal';
import { Fish, Heart, MessageCircle, MapPin, Ruler, Calendar, Edit2, Trash2, MoreVertical, LogOut, Camera, Users, X, UserPlus, UserMinus, Pencil } from 'lucide-react';
import SettingsModal from '../components/SettingsModal';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_IMAGE_SIZE_MB = 5;

const Profile = ({ viewingUserId = null, openUserProfile, openChatWithUser, onBackToOwnProfile }) => {
  const { currentUser, userProfile, logout, loadUserProfile } = useAuth();
  const isOwnProfile = !viewingUserId || viewingUserId === currentUser?.uid;
  const [viewedUserProfile, setViewedUserProfile] = useState(null);
  const [isFollowingViewedUser, setIsFollowingViewedUser] = useState(false);
  const [followViewedLoading, setFollowViewedLoading] = useState(false);
  const [viewedUserNotFound, setViewedUserNotFound] = useState(false);
  const [stats, setStats] = useState({
    posts: 0,
    favorites: 0,
    followers: 0,
    following: 0
  });
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState(true);
  const [userPosts, setUserPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [likedPostsLoading, setLikedPostsLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showMenu, setShowMenu] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [listModal, setListModal] = useState(null); // 'followers' | 'following'
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [listUsers, setListUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const fileInputRef = React.useRef(null);

  // Likers modal (who liked my post)
  const [likersModalPostId, setLikersModalPostId] = useState(null);
  const [likersList, setLikersList] = useState([]);
  const [likersLoading, setLikersLoading] = useState(false);

  // Comments on my posts
  const [expandedCommentsPostId, setExpandedCommentsPostId] = useState(null);
  const [commentsByPostId, setCommentsByPostId] = useState({});
  const [commentsLoading, setCommentsLoading] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitLoading, setCommentSubmitLoading] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [commentMenuOpen, setCommentMenuOpen] = useState(null);

  useEffect(() => {
    loadData();
  }, [currentUser, viewingUserId]);

  useEffect(() => {
    if (activeTab === 'liked' && currentUser) {
      loadLikedPosts();
    }
  }, [activeTab, currentUser]);

  useEffect(() => {
    if (!listModal || !currentUser) return;
    const ids = listModal === 'followers' ? followersList : followingList;
    if (ids.length === 0) {
      setListUsers([]);
      setListLoading(false);
      return;
    }
    let cancelled = false;
    setListLoading(true);
    const load = async () => {
      try {
        const chunks = [];
        for (let i = 0; i < ids.length; i += 10) {
          chunks.push(ids.slice(i, i + 10));
        }
        const snaps = await Promise.all(
          chunks.map(chunk =>
            getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
          )
        );
        const userMap = new Map();
        snaps.forEach(snap => {
          snap.docs.forEach(d => {
            const data = d.data();
            userMap.set(d.id, {
              id: d.id,
              displayName: data.displayName || data.email || 'Angler',
              photoURL: data.photoURL || null
            });
          });
        });
        const ordered = ids.map(id => userMap.get(id)).filter(Boolean);
        if (!cancelled) setListUsers(ordered);
      } catch (e) {
        console.error('Error loading list users:', e);
        if (!cancelled) setListUsers([]);
      } finally {
        if (!cancelled) setListLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [listModal, followersList, followingList, currentUser]);

  useEffect(() => {
    if (!likersModalPostId || !currentUser) return;
    let cancelled = false;
    setLikersLoading(true);
    const load = async () => {
      try {
        const q = query(
          collection(db, 'likes'),
          where('postId', '==', likersModalPostId)
        );
        const snap = await getDocs(q);
        const userIds = [...new Set(snap.docs.map(d => d.data().userId).filter(Boolean))];
        if (userIds.length === 0 || cancelled) {
          if (!cancelled) setLikersList([]);
          return;
        }
        const chunks = [];
        for (let i = 0; i < userIds.length; i += 10) {
          chunks.push(userIds.slice(i, i + 10));
        }
        const userSnaps = await Promise.all(
          chunks.map(chunk =>
            getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk))))
        );
        const userMap = new Map();
        userSnaps.forEach(s => {
          s.docs.forEach(d => {
            const data = d.data();
            userMap.set(d.id, {
              id: d.id,
              displayName: data.displayName || data.email || 'Angler',
              photoURL: data.photoURL || null
            });
          });
        });
        const ordered = userIds.map(id => userMap.get(id)).filter(Boolean);
        if (!cancelled) setLikersList(ordered);
      } catch (e) {
        console.error('Error loading likers:', e);
        if (!cancelled) setLikersList([]);
      } finally {
        if (!cancelled) setLikersLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [likersModalPostId, currentUser]);

  const loadComments = async (postId) => {
    if (!postId) return;
    setCommentsLoading(postId);
    try {
      const commentsRef = collection(db, 'posts', postId, 'comments');
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setCommentsByPostId(prev => ({ ...prev, [postId]: list }));
    } catch (error) {
      console.error('Error loading comments:', error);
      setCommentsByPostId(prev => ({ ...prev, [postId]: [] }));
    } finally {
      setCommentsLoading(null);
    }
  };

  const toggleComments = (postId) => {
    const next = expandedCommentsPostId === postId ? null : postId;
    setExpandedCommentsPostId(next);
    setCommentText('');
    if (next && !commentsByPostId[next]) loadComments(next);
  };

  const submitComment = async (postId) => {
    if (!currentUser || !commentText.trim() || commentSubmitLoading) return;
    setCommentSubmitLoading(true);
    try {
      const commentsRef = collection(db, 'posts', postId, 'comments');
      const newComment = {
        userId: currentUser.uid,
        userName: userProfile?.displayName || currentUser.displayName || 'Anonymous Angler',
        text: commentText.trim(),
        createdAt: serverTimestamp()
      };
      const docRef = await addDoc(commentsRef, newComment);
      await updateDoc(doc(db, 'posts', postId), { comments: increment(1) });
      const added = {
        id: docRef.id,
        ...newComment,
        createdAt: new Date()
      };
      setCommentsByPostId(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), added]
      }));
      setUserPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, comments: (p.comments || 0) + 1 } : p
      ));
      setCommentText('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setCommentSubmitLoading(false);
    }
  };

  const handleEditComment = async (postId, commentId, newText) => {
    if (!currentUser || !newText.trim()) return;
    try {
      const commentRef = doc(db, 'posts', postId, 'comments', commentId);
      await updateDoc(commentRef, { text: newText.trim() });
      setCommentsByPostId(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).map(c =>
          c.id === commentId ? { ...c, text: newText.trim() } : c
        )
      }));
      setEditingComment(null);
      setEditCommentText('');
      setCommentMenuOpen(null);
    } catch (error) {
      console.error('Error editing comment:', error);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!currentUser) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      const commentRef = doc(db, 'posts', postId, 'comments', commentId);
      await deleteDoc(commentRef);
      await updateDoc(doc(db, 'posts', postId), { comments: increment(-1) });
      setCommentsByPostId(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).filter(c => c.id !== commentId)
      }));
      setUserPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, comments: Math.max(0, (p.comments || 0) - 1) } : p
      ));
      setEditingComment(null);
      setEditCommentText('');
      setCommentMenuOpen(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const startEditComment = (postId, comment) => {
    setEditingComment({ postId, commentId: comment.id });
    setEditCommentText(comment.text || '');
    setCommentMenuOpen(null);
  };

  const cancelEditComment = () => {
    setEditingComment(null);
    setEditCommentText('');
    setCommentMenuOpen(null);
  };

  const loadData = async () => {
    if (!currentUser) return;
    const uid = viewingUserId || currentUser.uid;
    setLoading(true);
    setViewedUserProfile(null);
    setViewedUserNotFound(false);
    try {
      if (viewingUserId) {
        const userSnap = await getDoc(doc(db, 'users', viewingUserId));
        if (userSnap.exists()) setViewedUserProfile(userSnap.data());
        else setViewedUserNotFound(true);
        const followSnap = await getDocs(query(
          collection(db, 'follows'),
          where('followerId', '==', currentUser.uid),
          where('followingId', '==', viewingUserId)
        ));
        setIsFollowingViewedUser(followSnap.size > 0);
      }
      const postsQuery = query(
        collection(db, 'posts'),
        where('userId', '==', uid)
      );
      const favQuery = query(
        collection(db, 'favorites'),
        where('userId', '==', uid)
      );
      const followersQuery = query(
        collection(db, 'follows'),
        where('followingId', '==', uid)
      );
      const followingQuery = query(
        collection(db, 'follows'),
        where('followerId', '==', uid)
      );

      const [postsSnap, favSnap, followersSnap, followingSnap] = await Promise.all([
        getDocs(postsQuery),
        getDocs(favQuery),
        getDocs(followersQuery),
        getDocs(followingQuery)
      ]);

      const posts = postsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const followerIds = followersSnap.docs.map(d => d.data().followerId);
      const followingIds = followingSnap.docs.map(d => d.data().followingId);

      setUserPosts(posts);
      setFollowersList(followerIds);
      setFollowingList(followingIds);
      setStats({
        posts: postsSnap.size,
        favorites: favSnap.size,
        followers: followersSnap.size,
        following: followingSnap.size
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLikedPosts = async () => {
    if (!currentUser) return;
    setLikedPostsLoading(true);
    try {
      const likesQuery = query(
        collection(db, 'likes'),
        where('userId', '==', currentUser.uid)
      );
      const likesSnap = await getDocs(likesQuery);
      const likeDocs = likesSnap.docs.map(d => ({
        postId: d.data().postId,
        createdAt: d.data().createdAt
      }));
      likeDocs.sort((a, b) => {
        const tA = a.createdAt?.toMillis?.() ?? (a.createdAt ? new Date(a.createdAt).getTime() : 0);
        const tB = b.createdAt?.toMillis?.() ?? (b.createdAt ? new Date(b.createdAt).getTime() : 0);
        return tB - tA;
      });
      const postIds = likeDocs.map(l => l.postId).filter(Boolean);
      if (postIds.length === 0) {
        setLikedPosts([]);
        setLikedPostsLoading(false);
        return;
      }
      const idChunks = [];
      for (let i = 0; i < postIds.length; i += 10) {
        idChunks.push(postIds.slice(i, i + 10));
      }
      const postsSnaps = await Promise.all(
        idChunks.map(chunk => {
          const q = query(collection(db, 'posts'), where(documentId(), 'in', chunk));
          return getDocs(q);
        })
      );
      const postMap = new Map();
      postsSnaps.forEach(snap => {
        snap.docs.forEach(d => {
          postMap.set(d.id, { id: d.id, ...d.data() });
        });
      });
      const ordered = postIds
        .filter(id => postMap.has(id))
        .map(id => postMap.get(id));
      setLikedPosts(ordered);
    } catch (error) {
      console.error('Error loading liked posts:', error);
      setLikedPosts([]);
    } finally {
      setLikedPostsLoading(false);
    }
  };

  const handleDeletePost = async (postId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      // Delete image from storage if exists
      if (imageUrl) {
        try {
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image:', error);
        }
      }

      // Delete post from Firestore
      await deleteDoc(doc(db, 'posts', postId));

      // Update local state
      setUserPosts(userPosts.filter(post => post.id !== postId));
      setStats(prev => ({ ...prev, posts: prev.posts - 1 }));
      setShowMenu(null);
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post. Please try again.');
    }
  };

  const handleEditPost = async (postData) => {
    if (!editingPost) return;

    try {
      let imageUrl = editingPost.imageUrl;

      // Handle image update
      if (postData.imageFile) {
        // Delete old image if exists
        if (editingPost.imageUrl) {
          try {
            const oldImageRef = ref(storage, editingPost.imageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.error('Error deleting old image:', error);
          }
        }

        // Upload new image
        const timestamp = Date.now();
        const fileName = `catches/${currentUser.uid}/${timestamp}_${postData.imageFile.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, postData.imageFile);
        imageUrl = await getDownloadURL(storageRef);
      } else if (!postData.keepExistingImage && editingPost.imageUrl) {
        // User removed the image
        try {
          const imageRef = ref(storage, editingPost.imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image:', error);
        }
        imageUrl = null;
      }

      // Update post in Firestore
      const postRef = doc(db, 'posts', editingPost.id);
      const updatedData = {
        content: postData.content,
        species: postData.species,
        length: postData.length,
        weight: postData.weight,
        location: postData.location,
        dateCaught: postData.dateCaught,
        imageUrl: imageUrl,
        updatedAt: new Date()
      };

      await updateDoc(postRef, updatedData);

      // Update local state
      setUserPosts(userPosts.map(post => 
        post.id === editingPost.id ? { ...post, ...updatedData } : post
      ));

      setEditingPost(null);
      setShowMenu(null);
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Failed to update post. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCatchDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getUserInitials = (profile) => {
    const p = profile !== undefined ? profile : getDisplayProfile();
    const name = p?.displayName || (isOwnProfile ? currentUser?.displayName : null) || '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getDisplayProfile = () => (isOwnProfile ? userProfile : viewedUserProfile) || {};
  const getDisplayName = () => {
    const p = getDisplayProfile();
    return p.displayName || (isOwnProfile ? currentUser?.displayName : null) || 'Angler';
  };
  const getDisplayPhoto = () => getDisplayProfile().photoURL;

  const handleFollowViewedUser = async () => {
    if (!currentUser || !viewingUserId || followViewedLoading) return;
    setFollowViewedLoading(true);
    try {
      await addDoc(collection(db, 'follows'), {
        followerId: currentUser.uid,
        followingId: viewingUserId,
        createdAt: serverTimestamp()
      });
      setIsFollowingViewedUser(true);
      setStats(prev => ({ ...prev, followers: prev.followers + 1 }));
      await loadUserProfile(currentUser.uid);
    } catch (e) {
      console.error('Error following user:', e);
    } finally {
      setFollowViewedLoading(false);
    }
  };
  const handleUnfollowViewedUser = async () => {
    if (!currentUser || !viewingUserId || followViewedLoading) return;
    setFollowViewedLoading(true);
    try {
      const q = query(
        collection(db, 'follows'),
        where('followerId', '==', currentUser.uid),
        where('followingId', '==', viewingUserId)
      );
      const snapshot = await getDocs(q);
      for (const d of snapshot.docs) await deleteDoc(doc(db, 'follows', d.id));
      setIsFollowingViewedUser(false);
      setStats(prev => ({ ...prev, followers: Math.max(0, prev.followers - 1) }));
      await loadUserProfile(currentUser.uid);
    } catch (e) {
      console.error('Error unfollowing user:', e);
    } finally {
      setFollowViewedLoading(false);
    }
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file || !currentUser) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      alert('Please choose a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      alert(`Image must be under ${MAX_IMAGE_SIZE_MB} MB.`);
      return;
    }

    setUploadingPhoto(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const storagePath = `profiles/${currentUser.uid}/avatar.${ext}`;
      const storageRef = ref(storage, storagePath);

      if (userProfile?.photoStoragePath) {
        try {
          const oldRef = ref(storage, userProfile.photoStoragePath);
          await deleteObject(oldRef);
        } catch (err) {
          console.warn('Could not delete previous avatar:', err);
        }
      }

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        photoURL: downloadURL,
        photoStoragePath: storagePath,
        updatedAt: new Date().toISOString()
      });

      await loadUserProfile(currentUser.uid);
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleRemoveProfilePhoto = async (e) => {
    e.stopPropagation();
    if (!currentUser || !userProfile?.photoURL) return;
    if (!window.confirm('Remove your profile picture?')) return;

    setUploadingPhoto(true);
    try {
      if (userProfile.photoStoragePath) {
        try {
          const storageRef = ref(storage, userProfile.photoStoragePath);
          await deleteObject(storageRef);
        } catch (err) {
          console.warn('Could not delete photo from storage:', err);
        }
      }

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        photoURL: deleteField(),
        photoStoragePath: deleteField(),
        updatedAt: new Date().toISOString()
      });

      await loadUserProfile(currentUser.uid);
    } catch (error) {
      console.error('Error removing profile photo:', error);
      alert('Failed to remove photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const profileMainContent = (!viewedUserNotFound || isOwnProfile) ? (
    <div className="profile-view-wrapper">
      <div className="profile-header">
            <div className="profile-header-content">
              <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            onChange={handleProfilePhotoChange}
            className="profile-photo-input"
            aria-label="Upload profile picture"
          />
          <div className="profile-avatar-group">
            <button
              type="button"
              className="profile-avatar-wrap"
              onClick={() => isOwnProfile && fileInputRef.current?.click()}
              disabled={uploadingPhoto || !isOwnProfile}
              aria-label={isOwnProfile ? (uploadingPhoto ? 'Uploading...' : 'Change profile picture') : undefined}
            >
              <div className="profile-avatar-large">
                {(isOwnProfile ? userProfile?.photoURL : getDisplayPhoto()) ? (
                  <img src={isOwnProfile ? userProfile?.photoURL : getDisplayPhoto()} alt="" className="profile-avatar-img" />
                ) : (
                  getUserInitials(isOwnProfile ? undefined : getDisplayProfile())
                )}
              </div>
              {isOwnProfile && (uploadingPhoto ? (
                <span className="profile-avatar-overlay">...</span>
              ) : (
                <span className="profile-avatar-badge" aria-hidden="true">
                  <Camera size={18} />
                </span>
              ))}
            </button>
            {isOwnProfile && userProfile?.photoURL && (
              <button
                type="button"
                className="profile-remove-photo"
                onClick={handleRemoveProfilePhoto}
                disabled={uploadingPhoto}
              >
                Remove photo
              </button>
            )}
          </div>
          <div className="profile-info">
            <h2 className="profile-name">{getDisplayName()}</h2>
            {(getDisplayProfile().bio || '').trim() && (
              <p className="profile-bio">{getDisplayProfile().bio}</p>
            )}
            <div className="profile-stats">
              <div className="profile-stat">
                <div className="profile-stat-value">{stats.posts}</div>
                <div className="profile-stat-label">Posts</div>
              </div>
              <div className="profile-stat">
                <div className="profile-stat-value">{stats.favorites}</div>
                <div className="profile-stat-label">Saved</div>
              </div>
              <button
                type="button"
                className="profile-stat profile-stat-clickable"
                onClick={() => setListModal('followers')}
                aria-label="View followers"
              >
                <div className="profile-stat-value">{stats.followers}</div>
                <div className="profile-stat-label">Followers</div>
              </button>
              <button
                type="button"
                className="profile-stat profile-stat-clickable"
                onClick={() => setListModal('following')}
                aria-label="View following"
              >
                <div className="profile-stat-value">{stats.following}</div>
                <div className="profile-stat-label">Following</div>
              </button>
            </div>
          </div>
          <div className="profile-header-actions">
            {isOwnProfile ? (
              <>
                <button
                  type="button"
                  onClick={() => setEditProfileOpen(true)}
                  className="profile-settings-btn"
                  aria-label="Edit profile"
                >
                  <Edit2 size={18} />
                  Edit Profile
                </button>
                <button
                  type="button"
                  onClick={() => logout()}
                  className="profile-logout-btn"
                  aria-label="Log out"
                >
                  <LogOut size={18} />
                  Log out
                </button>
              </>
            ) : openUserProfile ? (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="profile-follow-btn"
                  onClick={isFollowingViewedUser ? handleUnfollowViewedUser : handleFollowViewedUser}
                  disabled={followViewedLoading}
                >
                  {followViewedLoading ? '...' : isFollowingViewedUser ? <><UserMinus size={18} /> Following</> : <><UserPlus size={18} /> Follow</>}
                </button>
                {openChatWithUser && (
                  <button
                    type="button"
                    className="profile-follow-btn"
                    onClick={() => openChatWithUser(viewingUserId)}
                    style={{ background: 'var(--bg-gray-200)', color: 'var(--text-dark)' }}
                  >
                    <MessageCircle size={18} />
                    Message
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="profile-tabs">
        <button
          className={`profile-tab ${activeTab === 'posts' ? 'active' : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          {isOwnProfile ? 'My Posts' : 'Posts'}
        </button>
        {isOwnProfile ? (
          <button
            className={`profile-tab ${activeTab === 'liked' ? 'active' : ''}`}
            onClick={() => setActiveTab('liked')}
          >
            Liked Posts
          </button>
        ) : (
          <span className="profile-tab profile-tab-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="profile-content">
        {activeTab === 'posts' && (
          loading ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem' }}>⏳</div>
              <h3>Loading...</h3>
            </div>
          ) : userPosts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <Fish size={60} strokeWidth={1.5} />
              </div>
              <h3 className="empty-title">You haven't posted any catches yet</h3>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {userPosts.map(post => (
                <div key={post.id} className="post-card-container">
                  {/* Post Header with Menu */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.5rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: getDisplayPhoto() ? 'transparent' : 'linear-gradient(135deg, var(--primary-green), var(--accent-orange))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        overflow: 'hidden'
                      }}>
                        {getDisplayPhoto() ? (
                          <img src={getDisplayPhoto()} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          getUserInitials(getDisplayProfile())
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>
                          {post.userName || (isOwnProfile ? 'You' : getDisplayName())}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                          {formatDate(post.createdAt)}
                        </div>
                      </div>
                    </div>

                    {/* Post Menu */}
                    <div style={{ position: 'relative' }}>
                      {isOwnProfile && (
                        <>
                      <button
                        onClick={() => setShowMenu(showMenu === post.id ? null : post.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          color: 'var(--text-medium)'
                        }}
                      >
                        <MoreVertical size={20} />
                      </button>

                      {showMenu === post.id && (
                        <div className="post-menu">
                          <button
                            onClick={() => {
                              setEditingPost(post);
                              setShowMenu(null);
                            }}
                            className="post-menu-item"
                          >
                            <Edit2 size={16} />
                            Edit Post
                          </button>
                          <button
                            onClick={() => handleDeletePost(post.id, post.imageUrl)}
                            className="post-menu-item danger"
                          >
                            <Trash2 size={16} />
                            Delete Post
                          </button>
                        </div>
                      )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Post Image */}
                  {post.imageUrl && (
                    <div className="post-image-container">
                      <img src={post.imageUrl} alt="Catch" className="post-image" />
                    </div>
                  )}

                  <div style={{ padding: '0 1.5rem 1.5rem' }}>
                    {/* Post Content */}
                    <p style={{ marginBottom: '1rem', fontSize: '1rem', lineHeight: 1.6, color: 'var(--text-dark)' }}>
                      {post.content}
                    </p>

                    {/* Catch Details */}
                    {(post.species || post.length || post.weight || post.location || post.dateCaught) && (
                      <div style={{
                        background: 'var(--bg-gray-50)',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '0.75rem'
                      }}>
                        {post.species && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <Fish size={16} color="var(--primary-green)" />
                            <span><strong>Species:</strong> {post.species}</span>
                          </div>
                        )}
                        {post.length && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <Ruler size={16} color="var(--primary-green)" />
                            <span><strong>Length:</strong> {post.length}</span>
                          </div>
                        )}
                        {post.weight && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <span>⚖️</span>
                            <span><strong>Weight:</strong> {post.weight}</span>
                          </div>
                        )}
                        {post.location && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <MapPin size={16} color="var(--primary-green)" />
                            <span><strong>Location:</strong> {post.location}</span>
                          </div>
                        )}
                        {post.dateCaught && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <Calendar size={16} color="var(--primary-green)" />
                            <span><strong>Caught:</strong> {formatCatchDate(post.dateCaught)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Stats */}
                    <div style={{
                      display: 'flex',
                      gap: '1.5rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid var(--border-gray)'
                    }}>
                      {isOwnProfile && (post.likes || 0) > 0 ? (
                        <button
                          type="button"
                          onClick={() => setLikersModalPostId(post.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: 'var(--text-medium)',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            fontSize: 'inherit'
                          }}
                          aria-label="See who liked this"
                        >
                          <Heart size={18} />
                          <span>{post.likes || 0}</span>
                        </button>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-medium)' }}>
                          <Heart size={18} />
                          <span>{post.likes || 0}</span>
                        </div>
                      )}
                      {isOwnProfile ? (
                        <button
                          type="button"
                          onClick={() => toggleComments(post.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: expandedCommentsPostId === post.id ? 'var(--primary-green)' : 'var(--text-medium)',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            fontSize: 'inherit'
                          }}
                          aria-label="View comments"
                        >
                          <MessageCircle size={18} />
                          <span>{post.comments || 0}</span>
                        </button>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-medium)' }}>
                          <MessageCircle size={18} />
                          <span>{post.comments || 0}</span>
                        </div>
                      )}
                    </div>

                    {/* Comments section (own profile only) */}
                    {isOwnProfile && expandedCommentsPostId === post.id && (
                      <div style={{
                        marginTop: '1rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid var(--border-gray)'
                      }}>
                        {commentsLoading === post.id ? (
                          <p style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>Loading comments...</p>
                        ) : (
                          <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                              {(commentsByPostId[post.id] || []).length === 0 ? (
                                <p style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>No comments yet. Be the first!</p>
                              ) : (
                                (commentsByPostId[post.id] || []).map(c => {
                                  const createdAt = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
                                  const isOwnComment = currentUser && c.userId != null && String(c.userId) === String(currentUser.uid);
                                  const isEditing = editingComment?.postId === post.id && editingComment?.commentId === c.id;
                                  return (
                                    <div
                                      key={c.id}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '0.5rem',
                                        padding: '0.5rem 0',
                                        borderBottom: '1px solid var(--border-gray)'
                                      }}
                                    >
                                      <div style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, var(--primary-green), var(--accent-orange))',
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem'
                                      }}>
                                        {(c.userName || '?').charAt(0)}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dark)' }}>
                                            {c.userName || 'Anonymous'}
                                          </div>
                                          {isOwnComment && !isEditing && (
                                            <div style={{ position: 'relative' }}>
                                              <button
                                                type="button"
                                                onClick={() => setCommentMenuOpen(commentMenuOpen === `${post.id}-${c.id}` ? null : `${post.id}-${c.id}`)}
                                                style={{
                                                  background: 'none',
                                                  border: 'none',
                                                  padding: '0.25rem',
                                                  cursor: 'pointer',
                                                  color: 'var(--text-light)',
                                                  borderRadius: '4px'
                                                }}
                                                aria-label="Comment options"
                                              >
                                                <MoreVertical size={16} />
                                              </button>
                                              {commentMenuOpen === `${post.id}-${c.id}` && (
                                                <>
                                                  <div
                                                    style={{ position: 'fixed', inset: 0, zIndex: 10 }}
                                                    onClick={() => setCommentMenuOpen(null)}
                                                    aria-hidden="true"
                                                  />
                                                  <div
                                                    style={{
                                                      position: 'absolute',
                                                      right: 0,
                                                      top: '100%',
                                                      marginTop: '2px',
                                                      background: 'var(--bg-white)',
                                                      border: '1px solid var(--border-gray)',
                                                      borderRadius: '8px',
                                                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                      zIndex: 11,
                                                      minWidth: '100px'
                                                    }}
                                                  >
                                                    <button
                                                      type="button"
                                                      onClick={() => startEditComment(post.id, c)}
                                                      style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        width: '100%',
                                                        padding: '0.5rem 0.75rem',
                                                        border: 'none',
                                                        background: 'none',
                                                        cursor: 'pointer',
                                                        fontSize: '0.875rem',
                                                        color: 'var(--text-dark)',
                                                        textAlign: 'left'
                                                      }}
                                                    >
                                                      <Pencil size={14} /> Edit
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={() => handleDeleteComment(post.id, c.id)}
                                                      style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        width: '100%',
                                                        padding: '0.5rem 0.75rem',
                                                        border: 'none',
                                                        background: 'none',
                                                        cursor: 'pointer',
                                                        fontSize: '0.875rem',
                                                        color: '#DC2626',
                                                        textAlign: 'left',
                                                        borderTop: '1px solid var(--border-gray)'
                                                      }}
                                                    >
                                                      <Trash2 size={14} /> Delete
                                                    </button>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        {isEditing ? (
                                          <div style={{ marginTop: '0.25rem' }}>
                                            <input
                                              type="text"
                                              value={editCommentText}
                                              onChange={(e) => setEditCommentText(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleEditComment(post.id, c.id, editCommentText);
                                                if (e.key === 'Escape') cancelEditComment();
                                              }}
                                              autoFocus
                                              style={{
                                                width: '100%',
                                                padding: '0.5rem 0.75rem',
                                                border: '1px solid var(--border-gray)',
                                                borderRadius: '8px',
                                                fontSize: '0.9rem',
                                                marginBottom: '0.5rem'
                                              }}
                                            />
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                              <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => handleEditComment(post.id, c.id, editCommentText)}
                                                disabled={!editCommentText.trim()}
                                                style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                                              >
                                                Save
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={cancelEditComment}
                                                style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <>
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text-medium)', lineHeight: 1.4 }}>
                                              {c.text}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                                              {formatDate(createdAt)}
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            {currentUser && (
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                                <input
                                  type="text"
                                  placeholder="Add a comment..."
                                  value={commentText}
                                  onChange={(e) => setCommentText(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && submitComment(post.id)}
                                  style={{
                                    flex: 1,
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid var(--border-gray)',
                                    borderRadius: '8px',
                                    fontSize: '0.9rem'
                                  }}
                                />
                                <button
                                  className="btn btn-primary"
                                  onClick={() => submitComment(post.id)}
                                  disabled={!commentText.trim() || commentSubmitLoading}
                                  style={{ padding: '0.5rem 1rem' }}
                                >
                                  {commentSubmitLoading ? 'Posting...' : 'Post'}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === 'liked' && (
          likedPostsLoading ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem' }}>⏳</div>
              <h3>Loading...</h3>
            </div>
          ) : likedPosts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <Fish size={60} strokeWidth={1.5} />
              </div>
              <h3 className="empty-title">No liked posts yet</h3>
              <p className="empty-subtitle">Posts you like on the Community feed will appear here.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {likedPosts.map(post => (
                <div key={post.id} className="post-card-container">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', padding: '0 1.5rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, var(--primary-green), var(--accent-orange))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1.1rem'
                    }}>
                      {post.userName?.charAt(0) || 'A'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>
                        {post.userName || 'Anonymous Angler'}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                        {formatDate(post.createdAt)}
                      </div>
                    </div>
                  </div>

                  {post.imageUrl && (
                    <div className="post-image-container">
                      <img src={post.imageUrl} alt="Catch" className="post-image" />
                    </div>
                  )}

                  <div style={{ padding: '0 1.5rem 1.5rem' }}>
                    <p style={{ marginBottom: '1rem', fontSize: '1rem', lineHeight: 1.6, color: 'var(--text-dark)' }}>
                      {post.content}
                    </p>

                    {(post.species || post.length || post.weight || post.location || post.dateCaught) && (
                      <div style={{
                        background: 'var(--bg-gray-50)',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '0.75rem'
                      }}>
                        {post.species && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <Fish size={16} color="var(--primary-green)" />
                            <span><strong>Species:</strong> {post.species}</span>
                          </div>
                        )}
                        {post.length && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <Ruler size={16} color="var(--primary-green)" />
                            <span><strong>Length:</strong> {post.length}</span>
                          </div>
                        )}
                        {post.weight && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <span>⚖️</span>
                            <span><strong>Weight:</strong> {post.weight}</span>
                          </div>
                        )}
                        {post.location && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <MapPin size={16} color="var(--primary-green)" />
                            <span><strong>Location:</strong> {post.location}</span>
                          </div>
                        )}
                        {post.dateCaught && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <Calendar size={16} color="var(--primary-green)" />
                            <span><strong>Caught:</strong> {formatCatchDate(post.dateCaught)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{
                      display: 'flex',
                      gap: '1.5rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid var(--border-gray)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#EF4444' }}>
                        <Heart size={18} fill="currentColor" />
                        <span>{post.likes || 0}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-medium)' }}>
                        <MessageCircle size={18} />
                        <span>{post.comments || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

        </div>
  ) : null;

  return (
    <div className="profile-page">
      {!isOwnProfile && viewingUserId && !loading && viewedUserNotFound && (
        <div className="empty-state" style={{ padding: '3rem 1.5rem' }}>
          <p className="empty-subtitle">User not found.</p>
          <button type="button" className="btn btn-primary" onClick={onBackToOwnProfile}>
            Back to my profile
          </button>
        </div>
      )}
      {profileMainContent}

      <EditPostModal
        isOpen={!!editingPost}
        onClose={() => setEditingPost(null)}
        onSubmit={handleEditPost}
        post={editingPost}
      />
      <SettingsModal isOpen={editProfileOpen} onClose={() => setEditProfileOpen(false)} />
      {listModal && (
        <div className="modal-overlay" onClick={() => setListModal(null)}>
          <div className="follow-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="follow-list-modal-header">
              <h3 className="follow-list-modal-title">
                {listModal === 'followers' ? <><Users size={22} /> Followers</> : <><Users size={22} /> Following</>}
              </h3>
              <button type="button" className="modal-close-btn" onClick={() => setListModal(null)} aria-label="Close">
                <X size={24} />
              </button>
            </div>
            <div className="follow-list-modal-body">
              {listLoading ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <div style={{ fontSize: '2rem' }}>⏳</div>
                  <p>Loading...</p>
                </div>
              ) : listUsers.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <p className="empty-subtitle">{listModal === 'followers' ? 'No followers yet.' : 'Not following anyone yet.'}</p>
                </div>
              ) : (
                <ul className="follow-list">
                  {listUsers.map((u) => (
                    <li key={u.id} className="follow-list-item">
                      <button
                        type="button"
                        className="follow-list-item-btn"
                        onClick={() => { if (openUserProfile) { openUserProfile(u.id); setListModal(null); } }}
                      >
                        <div className="follow-list-avatar" style={{ background: u.photoURL ? 'transparent' : 'linear-gradient(135deg, var(--primary-green), var(--accent-orange))' }}>
                          {u.photoURL ? <img src={u.photoURL} alt="" /> : (u.displayName || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="follow-list-name">{u.displayName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {likersModalPostId && (
        <div className="modal-overlay" onClick={() => setLikersModalPostId(null)}>
          <div className="follow-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="follow-list-modal-header">
              <h3 className="follow-list-modal-title">
                <Heart size={22} style={{ color: 'var(--primary-green)' }} /> Liked by
              </h3>
              <button type="button" className="modal-close-btn" onClick={() => setLikersModalPostId(null)} aria-label="Close">
                <X size={24} />
              </button>
            </div>
            <div className="follow-list-modal-body">
              {likersLoading ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <div style={{ fontSize: '2rem' }}>⏳</div>
                  <p>Loading...</p>
                </div>
              ) : likersList.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <p className="empty-subtitle">No likes yet.</p>
                </div>
              ) : (
                <ul className="follow-list">
                  {likersList.map((u) => (
                    <li key={u.id} className="follow-list-item">
                      <button
                        type="button"
                        className="follow-list-item-btn"
                        onClick={() => { if (openUserProfile) { openUserProfile(u.id); setLikersModalPostId(null); } }}
                      >
                        <div className="follow-list-avatar" style={{ background: u.photoURL ? 'transparent' : 'linear-gradient(135deg, var(--primary-green), var(--accent-orange))' }}>
                          {u.photoURL ? <img src={u.photoURL} alt="" /> : (u.displayName || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="follow-list-name">{u.displayName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
